import React, { useEffect } from "react";
import "./TermsOfService.css";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

const TermsOfService = () => {
  useScrollAnimation();

  useEffect(() => {
    // Add animation classes to elements on load
    const animateElements = document.querySelectorAll(".animate-on-load");
    animateElements.forEach((element, index) => {
      element.style.animationDelay = `${0.2 + index * 0.1}s`;
    });
  }, []);

  const effectiveDate = "January 1, 2024";

  return (
    <>
      <Header />
      <div className="terms-of-service-page">
        {/* Hero Section */}
        <section className="terms-hero">
          <div className="hero-background">
            <div className="overlay-dark"></div>
          </div>
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title animate-on-load">
                Terms of <span className="text-accent">Service</span>
              </h1>
              <p className="hero-subtitle animate-on-load">
                Effective Date: {effectiveDate}
              </p>
            </div>
          </div>
        </section>

        {/* Terms Content */}
        <section className="terms-content">
          <div className="container">
            <div className="terms-container">
              {/* Introduction */}
              <div className="terms-section animate-on-scroll">
                <h2>1. Agreement to Terms</h2>
                <p>
                  By accessing or using the Avvoka Exports website ("Website")
                  and our services, you agree to be bound by these Terms of
                  Service ("Terms"). If you do not agree to these Terms, please
                  do not use our Website or services.
                </p>
                <p>
                  These Terms govern your access to and use of our Website,
                  products, and services offered by Avvoka Exports ("we," "us,"
                  or "our").
                </p>
              </div>

              {/* Definitions */}
              <div className="terms-section animate-on-scroll delay-1">
                <h2>2. Definitions</h2>
                <ul>
                  <li>
                    <strong>"Website"</strong> refers to avvokaexports.com and
                    all associated subdomains
                  </li>
                  <li>
                    <strong>"Services"</strong> refers to our agricultural
                    export services, product offerings, and related services
                  </li>
                  <li>
                    <strong>"User"</strong> refers to any individual or entity
                    accessing our Website or using our Services
                  </li>
                  <li>
                    <strong>"Content"</strong> refers to all information, text,
                    graphics, photos, and other materials available on our
                    Website
                  </li>
                  <li>
                    <strong>"Products"</strong> refers to the agricultural goods
                    we export, including fresh produce, tea, and coffee
                  </li>
                </ul>
              </div>

              {/* Services Description */}
              <div className="terms-section animate-on-scroll delay-2">
                <h2>3. Services Description</h2>
                <p>
                  Avvoka Exports provides agricultural export services,
                  including but not limited to:
                </p>
                <ul>
                  <li>Export of fresh produce, tea, and coffee from Kenya</li>
                  <li>Quality assurance and certification services</li>
                  <li>Logistics and shipping coordination</li>
                  <li>International trade compliance services</li>
                  <li>Customer support and consultation</li>
                </ul>
                <p>
                  We reserve the right to modify, suspend, or discontinue any
                  aspect of our Services at any time without prior notice.
                </p>
              </div>

              {/* User Accounts */}
              <div className="terms-section animate-on-scroll delay-3">
                <h2>4. User Accounts</h2>
                <h3>4.1 Account Creation</h3>
                <p>
                  To access certain features of our Services, you may need to
                  create an account. You agree to provide accurate, current, and
                  complete information during registration.
                </p>

                <h3>4.2 Account Security</h3>
                <p>
                  You are responsible for maintaining the confidentiality of
                  your account credentials and for all activities that occur
                  under your account. Notify us immediately of any unauthorized
                  use of your account.
                </p>

                <h3>4.3 Account Termination</h3>
                <p>
                  We reserve the right to suspend or terminate your account at
                  our sole discretion if we believe you have violated these
                  Terms or applicable laws.
                </p>
              </div>

              {/* Orders and Payments */}
              <div className="terms-section animate-on-scroll">
                <h2>5. Orders and Payments</h2>
                <h3>5.1 Order Acceptance</h3>
                <p>
                  All orders are subject to acceptance by Avvoka Exports. We
                  reserve the right to refuse or cancel any order for any
                  reason, including product availability, errors in product or
                  pricing information, or suspected fraud.
                </p>

                <h3>5.2 Pricing</h3>
                <p>
                  All prices are quoted in US Dollars unless otherwise
                  specified. Prices are subject to change without notice. We are
                  not responsible for pricing errors and reserve the right to
                  cancel orders arising from such errors.
                </p>

                <h3>5.3 Payment Terms</h3>
                <p>
                  Payment terms will be specified in your order confirmation.
                  Generally, we require:
                </p>
                <ul>
                  <li>50% deposit upon order confirmation</li>
                  <li>50% balance payment before shipment</li>
                  <li>Full payment for orders under $5,000</li>
                </ul>

                <h3>5.4 Taxes</h3>
                <p>
                  You are responsible for all taxes, duties, and customs fees
                  associated with your order, unless otherwise specified in
                  writing.
                </p>
              </div>

              {/* Shipping and Delivery */}
              <div className="terms-section animate-on-scroll delay-1">
                <h2>6. Shipping and Delivery</h2>
                <h3>6.1 Shipping Methods</h3>
                <p>
                  We use various shipping methods including air freight and sea
                  freight, depending on product type, destination, and customer
                  requirements.
                </p>

                <h3>6.2 Delivery Times</h3>
                <p>
                  Estimated delivery times are provided for guidance only. We
                  are not liable for delays caused by factors beyond our
                  control, including but not limited to:
                </p>
                <ul>
                  <li>Customs clearance delays</li>
                  <li>Weather conditions</li>
                  <li>Transportation disruptions</li>
                  <li>Government regulations</li>
                </ul>

                <h3>6.3 Risk of Loss</h3>
                <p>
                  Title and risk of loss pass to you upon delivery of products
                  to the carrier. You are responsible for filing any claims with
                  carriers for lost or damaged shipments.
                </p>
              </div>

              {/* Returns and Refunds */}
              <div className="terms-section animate-on-scroll delay-2">
                <h2>7. Returns and Refunds</h2>
                <h3>7.1 Quality Issues</h3>
                <p>
                  We guarantee the quality of our products. If you receive
                  products that do not meet quality standards, you must notify
                  us within 48 hours of receipt. We will arrange for inspection
                  and, if validated, provide appropriate compensation.
                </p>

                <h3>7.2 Refund Policy</h3>
                <p>
                  Refunds are processed on a case-by-case basis. Generally, we
                  offer:
                </p>
                <ul>
                  <li>Full refund for quality issues</li>
                  <li>Partial refund for minor defects</li>
                  <li>Product replacement when possible</li>
                </ul>

                <h3>7.3 Return Process</h3>
                <p>
                  Returns must be pre-authorized by our customer service team.
                  Unauthorized returns will not be accepted.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="terms-section animate-on-scroll delay-3">
                <h2>8. Intellectual Property</h2>
                <h3>8.1 Our Content</h3>
                <p>
                  All content on our Website, including text, graphics, logos,
                  and images, is the property of Avvoka Exports or our licensors
                  and is protected by intellectual property laws.
                </p>

                <h3>8.2 License Grant</h3>
                <p>
                  We grant you a limited, non-exclusive, non-transferable
                  license to access and use our Website and Services for
                  legitimate business purposes.
                </p>

                <h3>8.3 Restrictions</h3>
                <p>You may not:</p>
                <ul>
                  <li>
                    Copy, modify, or create derivative works of our content
                  </li>
                  <li>Use our trademarks without written permission</li>
                  <li>Reverse engineer or attempt to extract source code</li>
                  <li>
                    Use our Services for any illegal or unauthorized purpose
                  </li>
                </ul>
              </div>

              {/* User Conduct */}
              <div className="terms-section animate-on-scroll">
                <h2>9. User Conduct</h2>
                <p>You agree not to:</p>
                <ul>
                  <li>Use our Services for any unlawful purpose</li>
                  <li>Submit false or misleading information</li>
                  <li>Interfere with or disrupt our Services</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Use our Services to compete with us</li>
                  <li>Harass, abuse, or harm other users or our staff</li>
                </ul>
              </div>

              {/* Disclaimer of Warranties */}
              <div className="terms-section animate-on-scroll delay-1">
                <h2>10. Disclaimer of Warranties</h2>
                <p>
                  OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE
                  FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
                  INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul>
                  <li>Implied warranties of merchantability</li>
                  <li>Fitness for a particular purpose</li>
                  <li>Non-infringement of intellectual property rights</li>
                  <li>Accuracy, reliability, or completeness of content</li>
                </ul>
                <p>
                  We do not warrant that our Services will be uninterrupted,
                  secure, or error-free.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div className="terms-section animate-on-scroll delay-2">
                <h2>11. Limitation of Liability</h2>
                <p>
                  TO THE FULLEST EXTENT PERMITTED BY LAW, AVVOKA EXPORTS SHALL
                  NOT BE LIABLE FOR:
                </p>
                <ul>
                  <li>
                    Any indirect, incidental, special, or consequential damages
                  </li>
                  <li>Loss of profits, revenue, data, or use</li>
                  <li>
                    Damages resulting from events beyond our reasonable control
                  </li>
                  <li>Third-party conduct or content</li>
                </ul>
                <p>
                  Our total liability for any claims related to our Services
                  shall not exceed the amount paid by you for the specific
                  products or services giving rise to the claim.
                </p>
              </div>

              {/* Indemnification */}
              <div className="terms-section animate-on-scroll delay-3">
                <h2>12. Indemnification</h2>
                <p>
                  You agree to indemnify and hold harmless Avvoka Exports, our
                  officers, directors, employees, and agents from any claims,
                  damages, losses, liabilities, and expenses (including
                  reasonable attorneys' fees) arising out of or relating to:
                </p>
                <ul>
                  <li>Your use of our Services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of another party</li>
                  <li>Your violation of any applicable laws</li>
                </ul>
              </div>

              {/* Governing Law */}
              <div className="terms-section animate-on-scroll">
                <h2>13. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance
                  with the laws of Kenya, without regard to its conflict of law
                  principles. Any disputes arising from these Terms shall be
                  subject to the exclusive jurisdiction of the courts of
                  Nairobi, Kenya.
                </p>
              </div>

              {/* Dispute Resolution */}
              <div className="terms-section animate-on-scroll delay-1">
                <h2>14. Dispute Resolution</h2>
                <h3>14.1 Negotiation</h3>
                <p>
                  Parties agree to attempt to resolve any disputes through good
                  faith negotiation before pursuing other remedies.
                </p>

                <h3>14.2 Mediation</h3>
                <p>
                  If negotiation fails, parties agree to attempt mediation
                  through a mutually agreed-upon mediator before initiating
                  litigation.
                </p>

                <h3>14.3 Arbitration</h3>
                <p>
                  Any disputes not resolved through negotiation or mediation
                  shall be finally settled by binding arbitration in accordance
                  with the rules of the Kenyan Arbitration Association.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="terms-section animate-on-scroll delay-2">
                <h2>15. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. We
                  will notify you of significant changes by posting the updated
                  Terms on our Website and updating the effective date. Your
                  continued use of our Services after such changes constitutes
                  acceptance of the modified Terms.
                </p>
              </div>

              {/* Termination */}
              <div className="terms-section animate-on-scroll delay-3">
                <h2>16. Termination</h2>
                <p>
                  We may terminate or suspend your access to our Services
                  immediately, without prior notice or liability, for any
                  reason, including if you breach these Terms.
                </p>
                <p>
                  Upon termination, your right to use our Services will
                  immediately cease. Provisions of these Terms that by their
                  nature should survive termination shall survive, including but
                  not limited to ownership provisions, warranty disclaimers,
                  indemnity, and limitations of liability.
                </p>
              </div>

              {/* Miscellaneous */}
              <div className="terms-section animate-on-scroll">
                <h2>17. Miscellaneous</h2>
                <h3>17.1 Entire Agreement</h3>
                <p>
                  These Terms constitute the entire agreement between you and
                  Avvoka Exports regarding our Services and supersede all prior
                  agreements.
                </p>

                <h3>17.2 Severability</h3>
                <p>
                  If any provision of these Terms is held to be invalid or
                  unenforceable, the remaining provisions will remain in full
                  force and effect.
                </p>

                <h3>17.3 No Waiver</h3>
                <p>
                  Our failure to enforce any right or provision of these Terms
                  will not be considered a waiver of those rights.
                </p>

                <h3>17.4 Assignment</h3>
                <p>
                  You may not assign or transfer these Terms without our prior
                  written consent. We may assign our rights and obligations
                  under these Terms without restriction.
                </p>
              </div>

              {/* Contact Information */}
              <div className="terms-section animate-on-scroll delay-1">
                <h2>18. Contact Information</h2>
                <p>
                  If you have any questions about these Terms, please contact us
                  at:
                </p>
                <div className="contact-info">
                  <p>
                    <strong>Avvoka Exports</strong>
                  </p>
                  <p>Email: legal@avvokaexports.com</p>
                  <p>Phone: +254 700 123 456</p>
                  <p>Address: Nairobi, Kenya</p>
                  <p>Website: www.avvokaexports.com</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Acceptance Section */}
        <section className="acceptance-section">
          <div className="container">
            <div className="acceptance-content animate-on-scroll">
              <h2>Acceptance of Terms</h2>
              <p>
                By using our Website or Services, you acknowledge that you have
                read, understood, and agree to be bound by these Terms of
                Service.
              </p>
              <div className="acceptance-actions">
                <button className="btn btn-primary">
                  Download Terms (PDF)
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 10L12 15L17 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 15V3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button className="btn btn-secondary">
                  Contact Legal Department
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default TermsOfService;
